// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
// @ts-nocheck
import {
  Avatar,
  Button,
  Grid,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  Table,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import {
  IconUser,
  IconBriefcase,
  IconCash,
  IconBuildingBank,
  IconCalendar,
  IconCertificate,
  IconReceipt,
  IconEdit,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { DataTable, type Column } from '../../components/DataTable';
import { useEmployee } from '../../hooks/useEmployees';
import type { EmployeeDeduction, AccrualBalance, BankAccount, Qualification } from '../../types/employee';

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: employee, loading, error } = useEmployee(id!);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !employee) {
    return (
      <Alert color="red" title="Error">
        Failed to load employee details.
      </Alert>
    );
  }

  const deductionColumns: Column<EmployeeDeduction>[] = [
    { key: 'name', label: 'Name', sortable: true },
    {
      key: 'type',
      label: 'Type',
      render: (d) => d.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    },
    {
      key: 'calculationType',
      label: 'Calc Type',
      render: (d) => (d.calculationType === 'percentage' ? 'Percentage' : 'Fixed'),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (d) =>
        d.calculationType === 'percentage' ? (
          `${d.amount}%`
        ) : (
          <CurrencyDisplay amount={d.amount} size="sm" />
        ),
    },
    { key: 'preTax', label: 'Pre-Tax', render: (d) => (d.preTax ? 'Yes' : 'No') },
    {
      key: 'isActive',
      label: 'Status',
      render: (d) => <StatusBadge status={d.isActive ? 'active' : 'inactive'} />,
    },
  ];

  const accrualColumns: Column<AccrualBalance>[] = [
    { key: 'policyName', label: 'Policy' },
    {
      key: 'policyType',
      label: 'Type',
      render: (a) => a.policyType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    },
    { key: 'accrued', label: 'Accrued', render: (a) => `${a.accrued} ${a.unit}` },
    { key: 'used', label: 'Used', render: (a) => `${a.used} ${a.unit}` },
    { key: 'available', label: 'Available', render: (a) => `${a.available} ${a.unit}` },
    { key: 'pendingRequests', label: 'Pending', render: (a) => `${a.pendingRequests} ${a.unit}` },
  ];

  const bankColumns: Column<BankAccount>[] = [
    { key: 'bankName', label: 'Bank' },
    {
      key: 'accountType',
      label: 'Type',
      render: (b) => b.accountType.charAt(0).toUpperCase() + b.accountType.slice(1),
    },
    { key: 'accountNumberLast4', label: 'Account', render: (b) => `****${b.accountNumberLast4}` },
    {
      key: 'depositType',
      label: 'Deposit',
      render: (b) => {
        if (b.depositType === 'full') return 'Full Amount';
        if (b.depositType === 'fixed') return `$${b.depositAmount?.toFixed(2)}`;
        return `${b.depositAmount}%`;
      },
    },
    { key: 'isPrimary', label: 'Primary', render: (b) => (b.isPrimary ? 'Yes' : '') },
  ];

  const qualificationColumns: Column<Qualification>[] = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    {
      key: 'issueDate',
      label: 'Issue Date',
      render: (q) => (q.issueDate ? new Date(q.issueDate).toLocaleDateString() : '-'),
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      render: (q) => (q.expiryDate ? new Date(q.expiryDate).toLocaleDateString() : '-'),
    },
    { key: 'status', label: 'Status', render: (q) => <StatusBadge status={q.status} /> },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={`${employee.firstName} ${employee.lastName}`}
        breadcrumbs={[
          { label: 'Employees', to: '/admin/workforce/employees' },
          { label: `${employee.firstName} ${employee.lastName}` },
        ]}
        actions={
          <Button
            icon={<IconEdit size={16} />}
            onClick={() => navigate(`/employees/${id}/edit`)}
          >
            Edit
          </Button>
        }
      />

      {/* Employee Info Card */}
      <Paper withBorder p="lg" radius="md">
        <Group gap="lg" ta="flex-start">
          <Avatar size={80} radius="xl" src={employee.avatarUrl} color="blue">
            {employee.firstName[0]}
            {employee.lastName[0]}
          </Avatar>
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="sm">
              <Text size="xl" fw={700}>
                {employee.firstName}{' '}
                {employee.middleName ? `${employee.middleName} ` : ''}
                {employee.lastName}
              </Text>
              <StatusBadge status={employee.status} />
            </Group>
            <Text c="dimmed" size="sm">
              {employee.employeeNumber} | {employee.jobTitle}
            </Text>
            <Group gap="xl" mt="xs">
              <Text size="sm">
                <Text span fw={600}>
                  Department:
                </Text>{' '}
                {employee.departmentName}
              </Text>
              <Text size="sm">
                <Text span fw={600}>
                  Branch:
                </Text>{' '}
                {employee.branchName}
              </Text>
              {employee.managerName && (
                <Text size="sm">
                  <Text span fw={600}>
                    Manager:
                  </Text>{' '}
                  {employee.managerName}
                </Text>
              )}
            </Group>
          </Stack>
        </Group>
      </Paper>

      {/* Tabs */}
      <Tabs defaultValue="personal">
        <Tabs.List>
          <Tabs.Tab value="personal" icon={<IconUser size={16} />}>
            Personal
          </Tabs.Tab>
          <Tabs.Tab value="employment" icon={<IconBriefcase size={16} />}>
            Employment
          </Tabs.Tab>
          <Tabs.Tab value="deductions" icon={<IconCash size={16} />}>
            Deductions
          </Tabs.Tab>
          <Tabs.Tab value="bank" icon={<IconBuildingBank size={16} />}>
            Bank Accounts
          </Tabs.Tab>
          <Tabs.Tab value="accruals" icon={<IconCalendar size={16} />}>
            Accruals
          </Tabs.Tab>
          <Tabs.Tab value="qualifications" icon={<IconCertificate size={16} />}>
            Qualifications
          </Tabs.Tab>
          <Tabs.Tab value="payHistory" icon={<IconReceipt size={16} />}>
            Pay History
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="personal" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Text fw={600} mb="sm">
                  Contact Information
                </Text>
                <Table>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td fw={500} w={150}>
                        Email
                      </Table.Td>
                      <Table.Td>{employee.email}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td fw={500}>Phone</Table.Td>
                      <Table.Td>{employee.phone || '-'}</Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Text fw={600} mb="sm">
                  Address
                </Text>
                {employee.address ? (
                  <Stack gap={2}>
                    <Text size="sm">{employee.address.street1}</Text>
                    {employee.address.street2 && (
                      <Text size="sm">{employee.address.street2}</Text>
                    )}
                    <Text size="sm">
                      {employee.address.city}, {employee.address.state}{' '}
                      {employee.address.zipCode}
                    </Text>
                    <Text size="sm">{employee.address.country}</Text>
                  </Stack>
                ) : (
                  <Text size="sm" c="dimmed">
                    No address on file
                  </Text>
                )}
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper withBorder p="md">
                <Text fw={600} mb="sm">
                  Emergency Contact
                </Text>
                {employee.emergencyContact ? (
                  <Table>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td fw={500} w={150}>
                          Name
                        </Table.Td>
                        <Table.Td>{employee.emergencyContact.name}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Relationship</Table.Td>
                        <Table.Td>{employee.emergencyContact.relationship}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Phone</Table.Td>
                        <Table.Td>{employee.emergencyContact.phone}</Table.Td>
                      </Table.Tr>
                      {employee.emergencyContact.email && (
                        <Table.Tr>
                          <Table.Td fw={500}>Email</Table.Td>
                          <Table.Td>{employee.emergencyContact.email}</Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <Text size="sm" c="dimmed">
                    No emergency contact on file
                  </Text>
                )}
              </Paper>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="employment" pt="md">
          <Paper withBorder p="md">
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td fw={500} w={200}>
                    Employee Number
                  </Table.Td>
                  <Table.Td>{employee.employeeNumber}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Hire Date</Table.Td>
                  <Table.Td>
                    {new Date(employee.hireDate).toLocaleDateString()}
                  </Table.Td>
                </Table.Tr>
                {employee.terminationDate && (
                  <Table.Tr>
                    <Table.Td fw={500}>Termination Date</Table.Td>
                    <Table.Td>
                      {new Date(employee.terminationDate).toLocaleDateString()}
                    </Table.Td>
                  </Table.Tr>
                )}
                <Table.Tr>
                  <Table.Td fw={500}>Department</Table.Td>
                  <Table.Td>{employee.departmentName}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Branch</Table.Td>
                  <Table.Td>{employee.branchName}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Job Title</Table.Td>
                  <Table.Td>{employee.jobTitle}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Manager</Table.Td>
                  <Table.Td>{employee.managerName || '-'}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Pay Type</Table.Td>
                  <Table.Td>
                    {employee.payType.charAt(0).toUpperCase() +
                      employee.payType.slice(1)}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Pay Rate</Table.Td>
                  <Table.Td>
                    <Group gap={2}>
                      <CurrencyDisplay amount={employee.payRate} />
                      <Text size="sm">
                        {employee.payType === 'hourly' ? '/hr' : '/yr'}
                      </Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Pay Frequency</Table.Td>
                  <Table.Td>
                    {employee.payFrequency
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Filing Status</Table.Td>
                  <Table.Td>
                    {employee.filingStatus
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={500}>Allowances</Table.Td>
                  <Table.Td>{employee.allowances}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="deductions" pt="md">
          <DataTable<Record<string, unknown>>
            columns={deductionColumns as unknown as Column<Record<string, unknown>>[]}
            data={employee.deductions as unknown as Record<string, unknown>[]}
            getRowKey={(d) => d.id as string}
            emptyMessage="No deductions configured"
          />
        </Tabs.Panel>

        <Tabs.Panel value="bank" pt="md">
          <DataTable<Record<string, unknown>>
            columns={bankColumns as unknown as Column<Record<string, unknown>>[]}
            data={employee.bankAccounts as unknown as Record<string, unknown>[]}
            getRowKey={(b) => b.id as string}
            emptyMessage="No bank accounts on file"
          />
        </Tabs.Panel>

        <Tabs.Panel value="accruals" pt="md">
          <DataTable<Record<string, unknown>>
            columns={accrualColumns as unknown as Column<Record<string, unknown>>[]}
            data={employee.accruals as unknown as Record<string, unknown>[]}
            getRowKey={(a) => a.id as string}
            emptyMessage="No accrual balances"
          />
        </Tabs.Panel>

        <Tabs.Panel value="qualifications" pt="md">
          <DataTable<Record<string, unknown>>
            columns={qualificationColumns as unknown as Column<Record<string, unknown>>[]}
            data={employee.qualifications as unknown as Record<string, unknown>[]}
            getRowKey={(q) => q.id as string}
            emptyMessage="No qualifications on file"
          />
        </Tabs.Panel>

        <Tabs.Panel value="payHistory" pt="md">
          {/* TODO: Fetch pay stubs from usePayStubs({ employeeId: id }) and display in DataTable */}
          <Text c="dimmed" ta="center" py="xl">
            Pay history will be displayed here once pay stubs are available.
          </Text>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
